import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * UI mirror of the service worker's durable Background-Sync queue. The SW (src/sw.ts) owns the
 * authoritative queue in IndexedDB and broadcasts lifecycle events; this slice reflects them so the
 * shell can show what is queued / posting / posted / failed and offer a retry affordance.
 *
 * This slice is intentionally NOT persisted (see store/index.ts whitelist): the SW IndexedDB queue
 * is the source of truth, and the mount-time REQUEST_QUEUE_STATE handshake rehydrates this mirror.
 */

export type OfflineOperationStatus = "queued" | "replaying" | "posted" | "failed";

export interface OfflineOperation {
	clientId: string;
	failedAt: string | null;
	lastError: string | null;
	/** See `MutationMetadata.missionEntryId` (helpers/sw/requestHandling.ts) — set only for a queued
	 *  `AddBpfLotChange` recorded against a BPF cycle-count mission task. Drives the "pending sync"
	 *  task-card overlay via `selectPendingCycleCountEntryIds` below. */
	missionEntryId?: string;
	mutationName: string;
	postedAt: string | null;
	queuedAt: string;
	status: OfflineOperationStatus;
}

export interface OfflineQueueState {
	operations: OfflineOperation[];
	/**
	 * Monotonic counter bumped by `cycleCountReconcileRequested` whenever `NetworkStatusProvider`
	 * decides a cycle-count replay resolved and the read cache must reconcile. `useCycleCountMissionTasks`
	 * watches this to reset its auto-paging cursor back to page 1 BEFORE the `CycleCountData` tag
	 * invalidation's automatic refetch runs — otherwise that refetch reuses whatever cursor the hook's
	 * auto-pager last advanced to (a later page, once a mission runs past `PAGE_SIZE`), the paginated
	 * `merge`'s append branch fires instead of its page-1 replace branch, and an already-known task's
	 * stale fields are never picked up (Atlas: offline read cache / reconcile-on-reconnect).
	 */
	reconcileRequestId: number;
	replaying: boolean;
}

const initialState: OfflineQueueState = {
	operations: [],
	reconcileRequestId: 0,
	replaying: false
};

const upsert = (
	operations: OfflineOperation[],
	clientId: string,
	mutationName: string,
	patch: Partial<OfflineOperation>,
	missionEntryId?: string
): OfflineOperation[] => {
	const index = operations.findIndex((op) => op.clientId === clientId);
	if (index === -1) {
		return [
			...operations,
			{
				clientId,
				failedAt: null,
				lastError: null,
				missionEntryId,
				mutationName,
				postedAt: null,
				queuedAt: new Date().toISOString(),
				status: "queued",
				...patch
			}
		];
	}
	// `missionEntryId` is set once at enqueue time and re-asserted defensively on every later
	// broadcast for the same operation (success/failed also carry it) — it never needs clearing.
	return operations.map((op) =>
		op.clientId === clientId ? { ...op, ...(missionEntryId ? { missionEntryId } : {}), ...patch } : op
	);
};

const offlineQueueSlice = createSlice({
	name: "offlineQueue",
	initialState,
	reducers: {
		queueReplayStarted: (state) => {
			state.replaying = true;
			state.operations = state.operations.map((op) =>
				op.status === "queued" || op.status === "failed" ? { ...op, status: "replaying" } : op
			);
		},
		queueReplayComplete: (state) => {
			state.replaying = false;
		},
		operationEnqueued: (
			state,
			action: PayloadAction<{ clientId: string; missionEntryId?: string; mutationName: string }>
		) => {
			const { clientId, missionEntryId, mutationName } = action.payload;
			state.operations = upsert(
				state.operations,
				clientId,
				mutationName,
				{ failedAt: null, lastError: null, postedAt: null, status: "queued" },
				missionEntryId
			);
		},
		operationPosted: (
			state,
			action: PayloadAction<{ clientId: string; missionEntryId?: string; mutationName: string }>
		) => {
			const { clientId, missionEntryId, mutationName } = action.payload;
			state.operations = upsert(
				state.operations,
				clientId,
				mutationName,
				{ postedAt: new Date().toISOString(), status: "posted" },
				missionEntryId
			);
		},
		operationFailed: (
			state,
			action: PayloadAction<{ clientId: string; missionEntryId?: string; mutationName: string; reason: string }>
		) => {
			const { clientId, missionEntryId, mutationName, reason } = action.payload;
			state.operations = upsert(
				state.operations,
				clientId,
				mutationName,
				{ failedAt: new Date().toISOString(), lastError: reason, status: "failed" },
				missionEntryId
			);
		},
		queueSnapshotReceived: (
			state,
			action: PayloadAction<Array<{ clientId: string; missionEntryId?: string; mutationName: string }>>
		) => {
			let next = state.operations.filter((op) => op.status === "posted");
			for (const { clientId, missionEntryId, mutationName } of action.payload) {
				next = upsert(next, clientId, mutationName, {}, missionEntryId);
			}
			state.operations = next;
		},
		clearPostedOperations: (state) => {
			state.operations = state.operations.filter((op) => op.status !== "posted");
		},
		cycleCountReconcileRequested: (state) => {
			state.reconcileRequestId += 1;
		}
	}
});

export const {
	clearPostedOperations,
	cycleCountReconcileRequested,
	operationEnqueued,
	operationFailed,
	operationPosted,
	queueReplayComplete,
	queueReplayStarted,
	queueSnapshotReceived
} = offlineQueueSlice.actions;

/** Operations still awaiting a successful post (queued, replaying, or failed). */
export const isPendingOperation = (op: OfflineOperation): boolean => op.status !== "posted";

/** Operations whose last replay deterministically failed and need user attention. */
export const isFailedOperation = (op: OfflineOperation): boolean => op.status === "failed";

/** The mutation name a locally-recorded cycle count against a mission task queues under. */
export const CYCLE_COUNT_MUTATION_NAME = "AddBpfLotChange";

/**
 * The set of BPF cycle-count mission task `entryId`s with a count locally recorded and still
 * actively awaiting sync (queued or replaying) — drives the "Pending sync" task-card state
 * (`CycleCountTaskCard`). Deliberately EXCLUDES `failed`: a definitive business failure never
 * applied, so that task falls back to Incomplete and relies on the Activity page (`isFailedOperation`)
 * to surface the reason, rather than showing a stale "pending" badge for a write that will never
 * complete.
 */
export const selectPendingCycleCountEntryIds = (operations: OfflineOperation[]): Set<string> =>
	new Set(
		operations
			.filter(
				(op) =>
					op.mutationName === CYCLE_COUNT_MUTATION_NAME &&
					(op.status === "queued" || op.status === "replaying") &&
					!!op.missionEntryId
			)
			.map((op) => op.missionEntryId as string)
	);

export default offlineQueueSlice.reducer;
